import { addBreadcrumb } from '@sentry/minimal';
import { Severity } from '@sentry/types';

const isExecuteEvent = (logEvent) => logEvent.name.startsWith('execute');
const isQueryResourceEvent = (logEvent) => logEvent.name.startsWith('queryresource');
const isNetworkEvent = (logEvent) => logEvent.name.startsWith('network');
const CATEGORY = 'relay';
const errorsIsGraphQLError = (errors) => Array.isArray(errors) && errors.some((item) => 'message' in item);
/**
 * Runs on every Relay life cycle stages, passing through some context for this function to _react_ upon. Through this
 * you can pass an _optional_ {@link Options} to set some properties that get emitted to Sentry.
 */
const logFunction = ({ filterEvents } = {}) => (logEvent) => {
    var _a, _b;
    if (typeof filterEvents === 'function' && !filterEvents(logEvent))
        return;
    const category = `${CATEGORY}.${logEvent.name}`;
    if (isExecuteEvent(logEvent)) {
        const { transactionID } = logEvent;
        switch (logEvent.name) {
            case 'execute.info':
                addBreadcrumb({
                    type: 'info',
                    level: Severity.Info,
                    category,
                    data: {
                        transactionID,
                        info: logEvent.info,
                    },
                });
                break;
            default: {
                addBreadcrumb({
                    type: 'debug',
                    level: Severity.Debug,
                    category,
                    data: {
                        transactionID,
                    },
                });
                break;
            }
        }
    }
    else if (isQueryResourceEvent(logEvent)) {
        const { resourceID } = logEvent;
        switch (logEvent.name) {
            case 'queryresource.fetch': {
                const { node: { params }, variables, } = logEvent.operation.request;
                addBreadcrumb({
                    type: 'info',
                    level: Severity.Info,
                    category,
                    data: {
                        resourceID,
                        id: (_a = params.id) !== null && _a !== void 0 ? _a : params.cacheID,
                        kind: params.operationKind,
                        name: params.name,
                        variables,
                        shouldFetch: logEvent.shouldFetch ? 'yes' : 'no',
                        fetchPolicy: logEvent.fetchPolicy,
                        renderPolicy: logEvent.renderPolicy,
                        queryAvailability: logEvent.queryAvailability,
                    },
                });
                break;
            }
            default: {
                addBreadcrumb({
                    type: 'debug',
                    level: Severity.Debug,
                    category,
                    data: {
                        resourceID,
                    },
                });
                break;
            }
        }
    }
    else if (isNetworkEvent(logEvent)) {
        const { transactionID } = logEvent;
        switch (logEvent.name) {
            case 'network.start': {
                const params = logEvent.params;
                addBreadcrumb({
                    type: 'info',
                    level: Severity.Info,
                    category,
                    data: {
                        transactionID,
                        id: (_b = params.id) !== null && _b !== void 0 ? _b : params.cacheID,
                        kind: params.operationKind,
                        name: params.name,
                        variables: logEvent.variables,
                    },
                });
                break;
            }
            case 'network.error': {
                const data = {
                    transactionID,
                    name: logEvent.name,
                };
                let error = logEvent.error;
                if ('graphqlErrors' in error &&
                    errorsIsGraphQLError(error === null || error === void 0 ? void 0 : error.graphqlErrors)) {
                    data.errors = error.graphqlErrors;
                }
                addBreadcrumb({
                    type: 'error',
                    level: Severity.Error,
                    category,
                    data,
                });
                break;
            }
            case 'network.info': {
                const { info } = logEvent;
                addBreadcrumb({
                    type: 'info',
                    level: Severity.Info,
                    category,
                    data: {
                        transactionID,
                        info,
                    },
                });
                break;
            }
            case 'network.complete': {
                addBreadcrumb({
                    type: 'info',
                    level: Severity.Info,
                    category,
                    data: {
                        transactionID,
                    },
                });
                break;
            }
            default: {
                addBreadcrumb({
                    type: 'debug',
                    level: Severity.Debug,
                    category,
                    data: {
                        transactionID,
                    },
                });
                break;
            }
        }
    }
    else {
        addBreadcrumb({
            type: 'debug',
            level: Severity.Debug,
            category,
            data: {},
        });
    }
};

export { logFunction };
